import React from 'react';
import type { BodySizeKeys } from '@noths/polaris-client-ribbons-base';
import { theme } from '@noths/polaris-client-ribbons-base';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { RuleSet } from 'styled-components';

import { IconClose } from '../../foundations/Iconography/IconClose';
import { IconError } from '../../foundations/Iconography/IconError';
import { IconInfo } from '../../foundations/Iconography/IconInfo';
import { IconWarning } from '../../foundations/Iconography/IconWarning';
import * as styles from './Notification.styles';

export interface BaseNotificationProps {
  anchored?: boolean;
  children: React.ReactNode;
  status: 'info' | 'alert' | 'warning' | 'success';
  textSize?: BodySizeKeys;
}

export interface ExtendedNotificationProps extends BaseNotificationProps {
  onClose: VoidFunction;
}

export type NotificationProps = Omit<BaseNotificationProps, 'status'>;

export type AnchoredNotificationProps = Omit<ExtendedNotificationProps, 'status'>;

const DEFAULT_TEXT_SIZE = 'SM';

// 'info' has no style of its own and falls back to the default.
const statusStyles: Partial<Record<BaseNotificationProps['status'], RuleSet<object>>> =
  styles.status;

const renderIcon = (status: string) => {
  switch (status) {
    case 'info':
      return <IconInfo css={styles.icon} height={24} width={24} />;
    case 'alert':
      return (
        <IconError color={`var(--neutral-black-base)`} css={styles.icon} height={24} width={24} />
      );
    case 'warning':
      return <IconWarning css={styles.icon} height={24} width={24} />;
    default:
      return null;
  }
};

export const AnchoredNotification = ({
  children,
  onClose,
  status,
  textSize = DEFAULT_TEXT_SIZE,
}: ExtendedNotificationProps) => (
  <div css={[styles.wrapper, statusStyles[status] || styles.status.default, styles.anchored]}>
    {renderIcon(status)}
    <div css={[styles.message, theme.responsiveText.BODY[textSize]]}>{children}</div>
    <button css={styles.iconButton} onClick={onClose} tabIndex={0}>
      <IconClose css={styles.icon} height={24} width={24} />
      <span css={visuallyHidden}>Close Button</span>
    </button>
  </div>
);

export const Notification = ({
  children,
  status,
  textSize = DEFAULT_TEXT_SIZE,
}: BaseNotificationProps) => (
  <div css={[styles.wrapper, statusStyles[status] || styles.status.default]}>
    {renderIcon(status)}
    <div css={[styles.message, theme.responsiveText.BODY[textSize]]}>{children}</div>
  </div>
);
